<template>
  <VModal :showing="showing" container-classes="min-w-192" data-testid="model-selector" @update:showing="$emit('close')">
    <template #header>
      <h2 class="text-lg font-medium text-gray-900 mb-0">Choose Model</h2>
      <p class="text-sm text-gray-700 mb-0 capitalize">Create 3D Slide</p>
    </template>
    <template #default>
      <div v-if="hasNoScenes" class="text-gray-600 font-light text-center max-w-xs mx-auto mt-10 mb-16">
        <p class="text-xl mb-4">No 3D models available yet!</p>
        <p class="mb-0">Please contact your VRIFY support team to track the completion of your 3D models</p>
      </div>
      <VSearchableTable
        v-else
        ref="tableRef"
        v-model:search="search"
        class="model-selector"
        :auto-load="true"
        :options="modelList"
        :current-item="modelOption"
        placeholder="Search by Model ID or Name"
        :disabled="disabled"
        :loading="loading"
        :count="count"
        @selected="onSelect"
      >
        <template #current="{ item }">
          <div>{{ item.name }}</div>
        </template>
        <template #headers>
          <th class="pl-6" @click="sortOn('id')">
            Model ID
            <FontAwesomeIcon v-if="sortKey === 'id'" class="ml-2" :icon="sortDir === '-' ? 'caret-up' : 'caret-down'" />
          </th>
          <th @click="sortOn('title')">
            Model Name
            <FontAwesomeIcon v-if="sortKey === 'title'" class="ml-2" :icon="sortDir === '-' ? 'caret-up' : 'caret-down'" />
          </th>
          <th class="pr-6 text-right" @click="sortOn('updated_at')">
            Last Updated
            <FontAwesomeIcon v-if="sortKey === 'updated_at'" class="ml-2" :icon="sortDir === '-' ? 'caret-up' : 'caret-down'" />
          </th>
        </template>
        <template #option="{ item }">
          <td class="pl-6">{{ item.id }}</td>
          <td>{{ (item as Model3D).title }}</td>
          <td class="pr-6 text-right font-normal text-sm whitespace-nowrap">{{ formatDateTimeMed((item as Model3D).updated_at) }}</td>
        </template>
      </VSearchableTable>
    </template>
    <template #footer>
      <slot name="footer">
        <div class="flex justify-end gap-2">
          <VButton @click="$emit('close')">Cancel</VButton>
        </div>
      </slot>
    </template>
  </VModal>
</template>

<script lang="ts" setup>
import { nextTick, ref, computed, onMounted, watch } from 'vue'

import type { Model3D } from '@/entities/model3d'
import { debounce } from '@/lib/utils'
import { formatDateTimeMed } from '@/lib/datetime-format-utils'

const RESULT_LIMIT = 100

const props = defineProps({
  showing: {
    type: Boolean,
    default: false,
  },
  companyId: {
    type: [String, Number],
    required: true,
  },
  accessType: {
    type: String,
    default: 'edit',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['select-model', 'cancel', 'close'])

const search = ref('')
const loading = ref(true)
const models = ref<Model3D[]>([])
const count = ref(0)
const hasError = ref(false)
const modelOption = ref<Model3D | null>(null)
const sortKey = ref('updated_at')
const sortDir = ref<'' | '-'>('-')
const tableRef = ref<any | undefined>()
const initialSceneList = ref<Model3D[]>([])
const modelList = computed(() => models.value ?? [])
const hasNoScenes = computed(() => !loading.value && initialSceneList.value.length <= 0)

let identifier: string
const fetchModels = async () => {
  loading.value = true
  models.value = []
  count.value = 0
  hasError.value = false

  let thisIdentifier: string

  // Cache values that determine response so we can confirm we're still valid later
  identifier = thisIdentifier = `${props.companyId}:${search.value}:${sortDir.value}${sortKey.value}`
  try {
    const data = await $fetch<{ results: any[]; count: number }>(`v2/companies/${props.companyId}/3dmodels`, {
      ...useFetchSharedOptions(),
      params: {
        limit: RESULT_LIMIT,
        archived: false,
        sort_by: `${sortDir.value}${sortKey.value}`,
        query: search.value,
        include: ['nodes'],
      },
    })

    if (identifier === thisIdentifier) {
      models.value = data.results
      count.value = data.count ?? 0

      // Make sure selected value is within viewport
      nextTick(() => {
        tableRef.value?.$el.querySelector('.selected')?.scrollIntoView({ block: 'center' })
      })
    }
  } catch (e) {
    hasError.value = true
  } finally {
    if (identifier === thisIdentifier) {
      loading.value = false
    }
  }
}

onMounted(async () => {
  await fetchModels()
  initialSceneList.value = [...models.value]
})

watch([sortKey, sortDir], ([key, dir], [oldKey, oldDir]) => {
  if (oldKey !== key || oldDir !== dir) fetchModels()
})

const sortOn = (key: string) => {
  if (key === sortKey.value) {
    sortDir.value = sortDir.value === '' ? '-' : ''
    return
  }
  sortKey.value = key
  sortDir.value = ''
}

const debouncedFetch = debounce(fetchModels, 500)
watch([() => props.companyId, search], () => {
  loading.value = true // Set loading to true immediately
  debouncedFetch()
})

function onSelect(selectedModel: Model3D): void {
  // Save the selected model so we can avoid fetching it later
  modelOption.value = selectedModel
  emit('select-model', selectedModel)
}
</script>

<style lang="stylus" scoped>
.model-selector
  & :deep(th)
    @apply text-xs uppercase text-gray-700 bg-gray-50 px-4 py-3 font-medium border-l-0 cursor-pointer
  & :deep(td)
    @apply text-gray-800 p-4 border-0
  & :deep(tbody tr:nth-child(even) td)
    @apply bg-gray-50
  & :deep(tbody tr:hover td)
    @apply bg-gray-200
</style>
